import { useEffect, useRef } from 'react';

const W = 600;
const H = 620;

// Arm segment lengths
const L1 = 110; // lower arm
const L2 = 90;  // upper arm
const L3 = 55;  // forearm

// Base pivot position
const BASE = { x: 200, y: 340 };

// Can position (right side, on surface)
const CAN_X = 330;
const CAN_Y = 565;
const CAN_W = 28;
const CAN_H = 48;

// Drop position — above the can's original position
const DROP_X = CAN_X;
const DROP_Y = CAN_Y - 400;

function lerp(a, b, t) { return a + (b - a) * t; }

function easeInOut(t) { return t < 0.5 ? 2*t*t : -1+(4-2*t)*t; }

// Solve 2-joint IK for target (tx, ty) relative to base
function solveIK(tx, ty, l1, l2) {
  const dist = Math.sqrt(tx*tx + ty*ty);
  const d = Math.min(dist, l1 + l2 - 1);
  const a1 = Math.atan2(ty, tx);
  const a2 = Math.acos((l1*l1 + d*d - l2*l2) / (2*l1*d));
  const shoulder = a1 - a2;
  const elbow = Math.atan2(ty - l1*Math.sin(shoulder), tx - l1*Math.cos(shoulder));
  return { shoulder, elbow };
}

// Animation keyframes: each phase has target gripper pos, gripper open amount, duration
const PHASES = [
  // 0: hover above can (target left edge of can)
  { tx: CAN_X - CAN_W/2 - BASE.x, ty: CAN_Y - BASE.y - 60, grip: 1, dur: 1.2 },
  // 1: descend to can mid-height
  { tx: CAN_X - CAN_W/2 - BASE.x, ty: CAN_Y - CAN_H/2 - BASE.y, grip: 1, dur: 0.7 },
  // 2: close gripper
  { tx: CAN_X - CAN_W/2 - BASE.x, ty: CAN_Y - CAN_H/2 - BASE.y, grip: 0, dur: 0.4 },
  // 3: lift
  { tx: CAN_X - CAN_W/2 - BASE.x, ty: CAN_Y - BASE.y - 80, grip: 0, dur: 0.8 },
  // 4: move to drop
  { tx: DROP_X - CAN_W/2 - BASE.x, ty: DROP_Y - BASE.y - 80, grip: 0, dur: 1.0 },
  // 5: descend to drop
  { tx: DROP_X - CAN_W/2 - BASE.x, ty: DROP_Y - CAN_H/2 - BASE.y, grip: 0, dur: 0.7 },
  // 6: open gripper
  { tx: DROP_X - CAN_W/2 - BASE.x, ty: DROP_Y - CAN_H/2 - BASE.y, grip: 1, dur: 0.4 },
  // 7: retract to home
  { tx: 80, ty: -120, grip: 1, dur: 1.0 },
  // 8: idle pause before next cycle (placed can fades, pickup can fades in)
  { tx: 80, ty: -120, grip: 1, dur: 1.2 },
];

const TOTAL = PHASES.reduce((s, p) => s + p.dur, 0);

export default function RoboticArm() {
  const canvasRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf;

    // Precompute phase start times
    const starts = [];
    let acc = 0;
    for (const p of PHASES) { starts.push(acc); acc += p.dur; }

    function getState(t) {
      const looped = t % TOTAL;
      let phaseIdx = PHASES.length - 1;
      for (let i = 0; i < PHASES.length; i++) {
        if (looped < starts[i] + PHASES[i].dur) { phaseIdx = i; break; }
      }
      const phase = PHASES[phaseIdx];
      const prev = PHASES[(phaseIdx - 1 + PHASES.length) % PHASES.length];
      const raw = (looped - starts[phaseIdx]) / phase.dur;
      const e = easeInOut(Math.max(0, Math.min(1, raw)));
      return {
        tx: lerp(prev.tx, phase.tx, e),
        ty: lerp(prev.ty, phase.ty, e),
        grip: lerp(prev.grip, phase.grip, e),
        // can is held when grip < 0.5 and we're past phase 2
        holding: phaseIdx >= 2 && phaseIdx <= 5 && phase.grip === 0,
        phaseIdx,
        e,
      };
    }

    function draw(ts) {
      if (!startRef.current) startRef.current = ts;
      const t = (ts - startRef.current) / 1000;

      ctx.clearRect(0, 0, W, H);

      const { tx, ty, grip, holding, phaseIdx, e } = getState(t);

      // Solve IK
      // We'll do a 3-segment chain: shoulder → elbow → wrist → tip
      // Simplified: treat as 2-segment to wrist, then fixed forearm
      const ik2 = solveIK(tx, ty, L1, L2);
      const sh = ik2.shoulder;
      const el = ik2.elbow;

      const p0 = BASE;
      const p1 = { x: p0.x + L1 * Math.cos(sh), y: p0.y + L1 * Math.sin(sh) };
      const p2 = { x: p1.x + L2 * Math.cos(el), y: p1.y + L2 * Math.sin(el) };
      // Forearm continues in same elbow direction + slight wrist angle
      const wristAngle = el + 0.3;
      const p3 = { x: p2.x + L3 * Math.cos(wristAngle), y: p2.y + L3 * Math.sin(wristAngle) };

      // Surface line
      ctx.strokeStyle = 'rgba(26,26,26,0.12)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(20, CAN_Y + CAN_H / 2 + 2);
      ctx.lineTo(W - 20, CAN_Y + CAN_H / 2 + 2);
      ctx.stroke();

      // Fridge at drop position (always visible)
      drawFridge(ctx, DROP_X + 19, DROP_Y + CAN_H / 2 + 4);

      // Can at rest (right) — fade in during phase 8, hidden while holding
      if (!holding) {
        const pickupAlpha = phaseIdx === 8 ? easeInOut(e) : (phaseIdx < 2 ? 1 : 0);
        ctx.globalAlpha = pickupAlpha;
        drawCan(ctx, CAN_X, CAN_Y);
        ctx.globalAlpha = 1;
      }

      // Held can — left edge at gripper tip, vertically centered on tip
      if (holding) {
        drawCan(ctx, p3.x + CAN_W / 2, p3.y + CAN_H);
      }

      // Draw arm segments
      const linkColor = '#1B4FD8';
      const jointColor = '#1B4FD8';

      // Lower arm
      drawLink(ctx, p0, p1, 10, linkColor);
      // Upper arm
      drawLink(ctx, p1, p2, 8, linkColor);
      // Forearm
      drawLink(ctx, p2, p3, 6, linkColor);

      // Joints
      drawJoint(ctx, p0, 13, jointColor);
      drawJoint(ctx, p1, 10, jointColor);
      drawJoint(ctx, p2, 8, jointColor);
      drawJoint(ctx, p3, 6, jointColor);

      // Base block
      drawBase(ctx, p0);

      // Gripper
      drawGripper(ctx, p3, wristAngle, grip);

      raf = requestAnimationFrame(draw);
    }

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="hidden lg:flex items-center justify-center flex-1">
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        aria-hidden="true"
        style={{ opacity: 0.75 }}
      />
    </div>
  );
}

function drawLink(ctx, a, b, width, color) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.globalAlpha = 0.9;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  // Inner highlight
  ctx.strokeStyle = 'rgba(255,255,255,0.15)';
  ctx.lineWidth = width * 0.35;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  ctx.restore();
}

function drawJoint(ctx, p, r, color) {
  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = 1;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.fill();
  // Inner ring
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  ctx.beginPath();
  ctx.arc(p.x, p.y, r * 0.45, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawBase(ctx, p) {
  ctx.save();
  ctx.fillStyle = '#1B4FD8';
  ctx.globalAlpha = 0.9;
  // Base trapezoid
  ctx.beginPath();
  ctx.moveTo(p.x - 28, p.y + 8);
  ctx.lineTo(p.x + 28, p.y + 8);
  ctx.lineTo(p.x + 20, p.y + 28);
  ctx.lineTo(p.x - 20, p.y + 28);
  ctx.closePath();
  ctx.fill();
  // Base rail
  ctx.fillStyle = 'rgba(26,26,26,0.3)';
  ctx.fillRect(p.x - 38, p.y + 28, 76, 8);
  ctx.restore();
}

function drawGripper(ctx, tip, angle, openAmount) {
  ctx.save();
  ctx.translate(tip.x, tip.y);
  ctx.rotate(angle + Math.PI / 2);

  const spread = openAmount * 14 + 4;
  const len = 22;

  ctx.strokeStyle = '#1B4FD8';
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.globalAlpha = 0.95;

  // Left finger
  ctx.beginPath();
  ctx.moveTo(-spread * 0.3, 0);
  ctx.lineTo(-spread, len);
  ctx.stroke();

  // Right finger
  ctx.beginPath();
  ctx.moveTo(spread * 0.3, 0);
  ctx.lineTo(spread, len);
  ctx.stroke();

  // Palm
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(-spread * 0.3, 0);
  ctx.lineTo(spread * 0.3, 0);
  ctx.stroke();

  ctx.restore();
}

function drawCan(ctx, x, y) {
  ctx.save();
  const cx = x;
  const cy = y - CAN_H / 2;
  const r = CAN_W / 2;

  // Can body
  ctx.fillStyle = '#e8e8e6';
  ctx.strokeStyle = 'rgba(26,26,26,0.2)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(cx - r, cy - CAN_H / 2, CAN_W, CAN_H, 4);
  ctx.fill();
  ctx.stroke();

  // Red label band
  ctx.fillStyle = '#cc2200';
  ctx.beginPath();
  ctx.rect(cx - r + 1, cy - 8, CAN_W - 2, 16);
  ctx.fill();

  // Highlight
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.beginPath();
  ctx.rect(cx - r + 3, cy - CAN_H / 2 + 4, 5, CAN_H - 8);
  ctx.fill();

  // Top ellipse
  ctx.fillStyle = '#d0d0ce';
  ctx.beginPath();
  ctx.ellipse(cx, cy - CAN_H / 2, r, r * 0.3, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawFridge(ctx, cx, cy) {
  const W = 52, H = 72;
  const x = cx - W / 2;
  const y = cy - H;
  const col = '#1B4FD8';

  ctx.save();
  ctx.globalAlpha = 0.9;

  // Body
  ctx.fillStyle = col;
  ctx.beginPath();
  ctx.roundRect(x, y, W, H, 5);
  ctx.fill();

  // Freezer compartment (top third)
  ctx.fillStyle = 'rgba(0,0,0,0.2)';
  ctx.beginPath();
  ctx.roundRect(x + 2, y + 2, W - 4, H * 0.3, [3, 3, 0, 0]);
  ctx.fill();

  // Fridge compartment (bottom two thirds)
  ctx.fillStyle = 'rgba(0,0,0,0.12)';
  ctx.beginPath();
  ctx.roundRect(x + 2, y + H * 0.3 + 3, W - 4, H * 0.68, [0, 0, 3, 3]);
  ctx.fill();

  // Divider line
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x, y + H * 0.32);
  ctx.lineTo(x + W, y + H * 0.32);
  ctx.stroke();

  // Freezer handle
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x + W * 0.3, y + H * 0.15);
  ctx.lineTo(x + W * 0.7, y + H * 0.15);
  ctx.stroke();

  // Fridge handle
  ctx.beginPath();
  ctx.moveTo(x + W * 0.3, y + H * 0.58);
  ctx.lineTo(x + W * 0.7, y + H * 0.58);
  ctx.stroke();

  // Subtle highlight on left edge
  ctx.strokeStyle = 'rgba(255,255,255,0.15)';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(x + 3, y + 6);
  ctx.lineTo(x + 3, y + H - 6);
  ctx.stroke();

  ctx.restore();
}
